// SVG/DOM resource icons for the React world — the player sidebar, the tile
// library, legends. The shapes and palette come from the shared single source of
// truth (resourceShapes.ts), which the board's Pixi tokens (game/PixiGame.ts)
// also build from, so a resource looks the same on the board and in the sidebar.

import {
  FOOD_RADIUS_FACTOR,
  RESOURCE_COLORS,
  RESOURCE_KINDS,
  RESOURCE_LABELS,
  resourceIconUrl,
  resourceOutline,
  type ResourceKind,
} from "./resourceShapes";

// Re-export the shared palette/labels so existing importers (App, TileLibrary)
// keep importing them from here.
export { RESOURCE_COLORS, RESOURCE_KINDS, RESOURCE_LABELS, resourceIconUrl };
export type { ResourceKind };

/** Presentation knobs both call sites pass through to the raw SVG element. */
export interface ResourceShapePresentation {
  fill?: string;
  fillOpacity?: number;
  stroke?: string;
  strokeWidth?: number;
  strokeDasharray?: string;
  strokeLinejoin?: "round" | "miter" | "bevel";
}

/** The bare icon shape for a resource — minerals = hex gem, food = disc, vigor =
 * heart, obsidian = diamond — built from the shared geometry. Presentation
 * (fill/stroke) is supplied by the caller, so the same shape serves the solid
 * sidebar icon and the tile library's permanent/one-time markers. */
export function ResourceShape({
  type,
  cx,
  cy,
  r,
  ...presentation
}: { type: string; cx: number; cy: number; r: number } & ResourceShapePresentation) {
  const outline = resourceOutline(type, r);
  if (!outline) {
    // food (and any unknown kind) — a disc
    return <circle cx={cx} cy={cy} r={r * FOOD_RADIUS_FACTOR} {...presentation} />;
  }
  const points = outline.map(([x, y]) => `${cx + x},${cy + y}`).join(" ");
  return <polygon points={points} {...presentation} />;
}

/** A self-contained inline resource icon for HTML/text contexts (e.g. the player
 * sidebar). Renders the pre-rendered PNG art (client/public/resources) so it
 * matches the board tokens exactly; `oneTime` selects the ghosted variant.
 *
 * The PNG art carries a wide transparent border (the glyph only fills ~45% of
 * the box), which otherwise reads as a big empty margin around every icon. A
 * size-proportional negative margin (`crop`) collapses that dead space so the
 * glyph hugs whatever sits next to it; pass `crop={0}` to opt out. */
export function ResourceIcon({
  kind,
  size = 12,
  oneTime = false,
  crop = 0.2,
}: {
  kind: ResourceKind;
  size?: number;
  oneTime?: boolean;
  crop?: number;
}) {
  return (
    <img
      className="res-icon"
      src={resourceIconUrl(kind, oneTime)}
      width={size}
      height={size}
      style={crop ? { margin: `${-size * crop}px` } : undefined}
      alt=""
      aria-hidden
      draggable={false}
    />
  );
}
